// Catálogo de cláusulas de trabajo, y las tareas que dispara cada una al
// aceptarse el presupuesto. Es la parte diferencial del proyecto.

import { api } from "../api";
import { esc } from "../html";
import { iconoMas, iconoChevron, iconoCerrar } from "../iconos";
import { ETIQUETA_CATEGORIA, ETIQUETA_TIPO_TAREA, ETIQUETA_DESTINATARIO } from "../etiquetas";
import { CATEGORIAS_CONDICION, TIPOS_TAREA, DESTINATARIOS } from "../../lib/tipos";
import type {
  CategoriaCondicion,
  Condicion,
  CondicionTarea,
  FichaCondicion,
} from "../../lib/tipos";

function filaPlantilla(t: Partial<CondicionTarea>): string {
  return `
    <div class="plantilla rounded-lg border border-borde bg-realce p-2.5">
      <div class="flex items-start gap-2">
        <input class="campo pTitulo flex-1" placeholder="Qué hay que hacer" maxlength="200"
               value="${esc(t.titulo_tarea)}" />
        <button type="button" class="pBorrar mt-1 shrink-0 text-apagado hover:text-alerta"
                aria-label="Sacar tarea">${iconoCerrar}</button>
      </div>
      <div class="mt-2 grid grid-cols-3 gap-2">
        <label><span class="etiqueta">Tipo</span>
          <select class="campo pTipo">
            ${TIPOS_TAREA.map(
              (x) =>
                `<option value="${x}"${x === (t.tipo ?? "TRAMITE") ? " selected" : ""}>${ETIQUETA_TIPO_TAREA[x]}</option>`,
            ).join("")}
          </select></label>
        <label><span class="etiqueta">A los días</span>
          <input class="campo pDias" type="number" min="0" max="365" value="${t.dias_offset ?? 7}" /></label>
        <label><span class="etiqueta">Para</span>
          <select class="campo pPara">
            ${DESTINATARIOS.map(
              (x) =>
                `<option value="${x}"${x === (t.para ?? "DESARROLLADOR") ? " selected" : ""}>${ETIQUETA_DESTINATARIO[x]}</option>`,
            ).join("")}
          </select></label>
      </div>
    </div>`;
}

function formulario(f: FichaCondicion): string {
  const c = f.condicion;
  return `
    <form class="fEditar space-y-3" data-id="${c.id}">
      <label><span class="etiqueta">Título</span>
        <input name="titulo" class="campo" value="${esc(c.titulo)}" maxlength="200" required /></label>
      <label><span class="etiqueta">Categoría</span>
        <select name="categoria" class="campo">
          ${CATEGORIAS_CONDICION.map(
            (x) =>
              `<option value="${x}"${x === c.categoria ? " selected" : ""}>${ETIQUETA_CATEGORIA[x]}</option>`,
          ).join("")}
        </select></label>
      <label><span class="etiqueta">Texto que va al presupuesto</span>
        <textarea name="texto" class="campo" rows="4" maxlength="4000" required>${esc(c.texto)}</textarea></label>

      <div>
        <span class="etiqueta">Al aceptarse, se crean estas tareas</span>
        <div class="plantillas space-y-2">${f.tareas.map(filaPlantilla).join("")}</div>
        <button type="button" class="bAgregar btn-sec mt-2 w-full text-sm">${iconoMas} Agregar tarea</button>
      </div>

      <p class="err text-sm text-alerta"></p>
      <div class="flex gap-2">
        <button class="btn-primario flex-1">Guardar</button>
        <button type="button" class="bBaja btn-sec" data-id="${c.id}">Sacar del catálogo</button>
      </div>
    </form>`;
}

export async function renderCondiciones(contenedor: HTMLElement): Promise<void> {
  async function pintar(): Promise<void> {
    const condiciones = await api.get<Condicion[]>("/api/condiciones");

    const porCategoria = CATEGORIAS_CONDICION.map((cat) => ({
      cat,
      lista: condiciones.filter((c) => c.categoria === cat),
    })).filter((g) => g.lista.length);

    contenedor.innerHTML = `
      <h1 class="titulo-pagina">Condiciones</h1>
      <p class="subtitulo">Las cláusulas que van con los presupuestos, y lo que disparan al firmarse.</p>

      ${
        condiciones.length
          ? porCategoria
              .map(
                (g) => `
        <section class="mt-5">
          <h2 class="mb-2 text-sm font-semibold uppercase tracking-wider text-apagado">
            ${ETIQUETA_CATEGORIA[g.cat]}
          </h2>
          <div class="space-y-2">
            ${g.lista
              .map(
                (c) => `
              <details class="fila" data-id="${c.id}">
                <summary class="flex items-center gap-3 p-3">
                  <div class="min-w-0 flex-1">
                    <div class="truncate">${esc(c.titulo)}</div>
                    ${
                      c.tareas
                        ? `<div class="mt-1"><span class="chip chip-acento">dispara ${c.tareas} ${
                            c.tareas === 1 ? "tarea" : "tareas"
                          }</span></div>`
                        : ""
                    }
                  </div>
                  <span class="shrink-0 text-apagado">${iconoChevron}</span>
                </summary>
                <div class="detalle border-t border-borde p-3 text-sm text-apagado">Cargando…</div>
              </details>`,
              )
              .join("")}
          </div>
        </section>`,
              )
              .join("")
          : `<div class="tarjeta mt-4 text-center text-apagado">
               Todavía no hay cláusulas. Podés cargar las habituales de una con
               <code class="text-acento">seeds/condiciones.sql</code>.
             </div>`
      }

      <details class="tarjeta mt-5">
        <summary class="flex items-center gap-2 font-medium text-acento">${iconoMas} Cláusula nueva</summary>
        <form id="fNuevo" class="mt-4 space-y-3">
          <label><span class="etiqueta">Título</span>
            <input id="titulo" class="campo" required maxlength="200" /></label>
          <label><span class="etiqueta">Categoría</span>
            <select id="categoria" class="campo">
              ${CATEGORIAS_CONDICION.map(
                (x) => `<option value="${x}">${ETIQUETA_CATEGORIA[x]}</option>`,
              ).join("")}
            </select></label>
          <label><span class="etiqueta">Texto</span>
            <textarea id="texto" class="campo" rows="4" required maxlength="4000"></textarea></label>
          <p id="errNuevo" class="text-sm text-alerta"></p>
          <button class="btn-primario w-full">Crear</button>
        </form>
      </details>`;

    contenedor.querySelectorAll<HTMLDetailsElement>("details[data-id]").forEach((d) => {
      d.addEventListener("toggle", async () => {
        if (!d.open || d.dataset.cargado) return;
        d.dataset.cargado = "1";
        const caja = d.querySelector(".detalle") as HTMLElement;
        try {
          const f = await api.get<FichaCondicion>(`/api/condiciones/${d.dataset.id}`);
          caja.innerHTML = formulario(f);
          enganchar(caja);
        } catch (e) {
          caja.innerHTML = `<span class="text-alerta">${esc(
            e instanceof Error ? e.message : "error",
          )}</span>`;
        }
      });
    });

    const form = contenedor.querySelector("#fNuevo") as HTMLFormElement;
    form.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const err = contenedor.querySelector("#errNuevo") as HTMLElement;
      err.textContent = "";
      try {
        await api.post("/api/condiciones", {
          titulo: (contenedor.querySelector("#titulo") as HTMLInputElement).value.trim(),
          categoria: (contenedor.querySelector("#categoria") as HTMLSelectElement)
            .value as CategoriaCondicion,
          texto: (contenedor.querySelector("#texto") as HTMLTextAreaElement).value.trim(),
        });
        await pintar();
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo crear";
      }
    });
  }

  function enganchar(caja: HTMLElement): void {
    const form = caja.querySelector(".fEditar") as HTMLFormElement;
    const err = caja.querySelector(".err") as HTMLElement;
    const lista = caja.querySelector(".plantillas") as HTMLElement;

    function engancharBorrar(): void {
      lista.querySelectorAll<HTMLButtonElement>(".pBorrar").forEach((b) => {
        b.onclick = () => b.closest(".plantilla")?.remove();
      });
    }
    engancharBorrar();

    caja.querySelector(".bAgregar")?.addEventListener("click", () => {
      lista.insertAdjacentHTML("beforeend", filaPlantilla({}));
      engancharBorrar();
    });

    form.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      err.textContent = "";
      const d = new FormData(form);
      const tareas = [...lista.querySelectorAll<HTMLElement>(".plantilla")].map((el) => ({
        titulo_tarea: (el.querySelector(".pTitulo") as HTMLInputElement).value.trim(),
        tipo: (el.querySelector(".pTipo") as HTMLSelectElement).value,
        dias_offset: Number((el.querySelector(".pDias") as HTMLInputElement).value),
        para: (el.querySelector(".pPara") as HTMLSelectElement).value,
      }));

      if (tareas.some((t) => !t.titulo_tarea)) {
        err.textContent = "Hay una tarea sin título";
        return;
      }

      try {
        await api.put(`/api/condiciones/${form.dataset.id}`, {
          titulo: String(d.get("titulo") ?? "").trim(),
          categoria: d.get("categoria"),
          texto: String(d.get("texto") ?? "").trim(),
          tareas,
        });
        await pintar();
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo guardar";
      }
    });

    caja.querySelector(".bBaja")?.addEventListener("click", async () => {
      if (!confirm("¿Sacar la cláusula del catálogo? Los presupuestos que ya la llevan no cambian."))
        return;
      await api.del(`/api/condiciones/${form.dataset.id}`);
      await pintar();
    });
  }

  try {
    await pintar();
  } catch (e) {
    contenedor.innerHTML = `<div class="tarjeta text-alerta">No se pudieron cargar las condiciones: ${esc(
      e instanceof Error ? e.message : "error desconocido",
    )}</div>`;
  }
}
